
import {
    Form,
    Select,
    FormField,
    TableRow,
    TableHeaderCell,
    TableHeader,
    TableCell,
    TableBody,
    Table,
    Checkbox,
    Message,
    Icon, TextArea
} from "semantic-ui-react"
import { Link } from "react-router-dom";
import React, { useState, useEffect } from "react"
import { ActionButton, LoadingWrapper } from "../common"
import { filterDataByTeam, getCSRFToken, fetchWithAuth, useAuth } from "../auth";
import { MemberStatuses } from "../members/Members";
import { teamSubmitsReadiness } from "../common/teamSettings";
import { getTeamDetails as fetchTeamDetails } from "../common/apiClient";
import './Readiness.css'

export const Readiness = props => {
    const { isReadOnly } = useAuth()

    const [associatedAssets, setAssociatedAssets] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [teams, setTeams] = useState([])
    const [team, setTeam] = useState({})
    const [membersReadiness, setMembersReadiness] = useState({})
    const [assetReadiness, setAssetReadiness] = useState({})
    const [initialReadiness, setInitialReadiness] = useState({})
    const [teamStatus, setTeamStatus] = useState(true)
    const [teamReason, setTeamReason] = useState('')
    const [teamReadinessNotes, setTeamReadinessNotes] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)

    const getTeams = async () => {
        setLoaded(false)
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'teams/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        })
        const respJson = await resp.json()
        const userInfo = filterDataByTeam()
        const eligibleTeams = !isReadOnly && userInfo?.filterData
            ? respJson.filter(team => team.members.includes(userInfo.name))
            : respJson
        const readinessTeams = (await Promise.all(eligibleTeams.map(async team => {
            try {
                const details = await fetchTeamDetails(team.id)
                return details?.id ? { ...team, ...details } : null
            } catch {
                return null
            }
        }))).filter(team => team && teamSubmitsReadiness(team))
        setTeams(readinessTeams)

        const savedTeam = sessionStorage.getItem('readinessTeam')
        if (savedTeam && readinessTeams.some(team => String(team.id) === savedTeam)) {
            getTeamDetails(savedTeam)
        } else {
            sessionStorage.removeItem('readinessTeam')
        }
        setLoaded(true)
    }

    const getAssets = async () => {
        setLoaded(false)
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'teams/' + team.id + "/assets/", {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        })
        const respJson = await resp.json()
        const newAssetReadiness = {}
        for (const asset of respJson) {
            newAssetReadiness[asset.id] = asset.status
        }
        setAssetReadiness(newAssetReadiness)
        setAssociatedAssets(respJson)
        setLoaded(true)
    }

    const getTeamDetails = async (id) => {
        setLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `teams/${id}/`, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        if (!teamSubmitsReadiness(data)) {
            setTeam({})
            setAssociatedAssets([])
            sessionStorage.removeItem('readinessTeam')
            setError('This team does not submit readiness.')
            setLoaded(true)
            return
        }
        const newMemberReadiness = {}
        for (const member of data.members) {
            if (!member.role.includes('Manager')) {
                newMemberReadiness[member.userId] = member.status.toLowerCase()
            }
        }
        setMembersReadiness(newMemberReadiness)
        setTeam(data)
        setLoaded(true)
    }

    const formIsValid = () => {
        if (teamStatus === false && !teamReason) {
            setError("A status reason is required when marking the team as not ready.")
            return false
        }
        return true
    }

    async function submitReadiness() {
        if (isReadOnly || !team.id || !teamSubmitsReadiness(team)) {
            setError('Readiness submission is not permitted for this team.')
            return
        }
        let readinesspayload = JSON.stringify({
            team: team.id,
            teamStatus: teamStatus ? 'ready' : 'not_ready',
            teamReason: teamReason,
            teamNotes: teamReadinessNotes,
            membersReadiness: membersReadiness,
            assetReadiness: assetReadiness
        })

        if (readinesspayload === initialReadiness) {
            console.log("No readiness changes")
            return
        }

        if (!formIsValid()) {
            window.scrollTo({ 'top': 0, behavior: 'smooth' })
            return
        }

        setLoaded(false)
        setSuccess(false)
        setError('')

        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'readiness/', {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                'X-CSRFToken': getCSRFToken()
            },

            credentials: 'include',
            body: readinesspayload
        })
        window.scrollTo({ 'top': 0, behavior: 'smooth' })
        if (!resp.ok) {
            setError('Something went wrong.')
        } else {
            setSuccess(true)
            setInitialReadiness(readinesspayload)
        }
        setLoaded(true)
    }

    async function handleTeamChange(event, data) {
        if (!data.value) {
            setTeam({})
            setAssociatedAssets([])
            sessionStorage.removeItem('readinessTeam')
        } else {
            getTeamDetails(data.value)
            sessionStorage.setItem('readinessTeam', data.value)
        }
    }

    const updateMemberReadiness = (id, value) => {
        const newMemberReadiness = structuredClone(membersReadiness)
        newMemberReadiness[id] = value
        setMembersReadiness(newMemberReadiness)
    }

    const updateAssetReadiness = (id, value) => {
        const newAssetReadiness = structuredClone(assetReadiness)
        newAssetReadiness[id] = value
        setAssetReadiness(newAssetReadiness)
    }

    useEffect(() => {
        getTeams();
    }, [])

    useEffect(() => {
        if (team.id) {
            getAssets()
        }
    }, [team])

    useEffect(() => {
        if (teamReason.length) {
            setTeamStatus(false)
        }
    }, [teamReason])


    const assetReadinessOptions = [
        {
            value: 'maintenance',
            key: 'maintenance',
            text: 'Maintenance'
        },
        {
            value: 'outOfService',
            key: 'outOfService',
            text: 'Out of Service/Repair'
        },
        {
            value: 'functional',
            key: 'functional',
            text: 'Functional'
        }
    ]

    const teamReadinessOptions = [
        {
            value: "shortstaffed",
            key: "shortstaffed",
            text: "Short-staffed"
        },
        {
            value: "equipmentIssues",
            key: "equipentIssues",
            text: "Equipment issues"
        }
    ]

    return (
        <LoadingWrapper loading={!loaded}>
            <div id="centered-form" align="center">
                {success && <Message positive>Success</Message>}
                {error && <Message negative>{error}</Message>}
                <div id="form-wrapper">
                    <Form style={{ width: "100%" }}>
                        <FormField required>
                            <label>Team</label>
                            <Select
                                search
                                options={
                                    teams.map((t) => {
                                        return {
                                            id: t.id,
                                            value: t.id,
                                            text: t.name
                                        }
                                    })
                                }
                                onChange={handleTeamChange}
                                value={team.id}
                                clearable />
                        </FormField>
                        <FormField disabled={!team.id}>
                            <label>Members</label>
                            {team.members?.length ? <Table celled striped unstackable textAlign="center">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>Member</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {team.members?.filter((member) => {
                                        return !(member.role.includes('Manager'))
                                    }).map((member) => {
                                        return <TableRow key={member.userId}>
                                            <TableCell>
                                                <Link to={'/members/' + member.userId + '/view'}>{member.name}</Link>
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    className="icct-disabled-selection"
                                                    style={{width: "100%"}}
                                                    options={MemberStatuses}
                                                    onChange={(event, data) => {
                                                        updateMemberReadiness(member.userId, data.value)
                                                    }}
                                                    value={membersReadiness[member.userId] ? membersReadiness[member.userId] : 'active'}/>
                                            </TableCell>
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table> : "No members"}
                            <div><Icon className="exclamation circle"/>To update a member's status, an Associate Manager
                                or above will need to add a calendar event for them.</div>
                        </FormField>
                        <FormField disabled={!team.id}>
                            <label>Assets</label>
                            {associatedAssets.length ? <Table celled striped compact unstackable textAlign="center">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell style={{ minWidth: "8rem" }}>Asset</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {associatedAssets.map((asset) => {
                                        return <TableRow style={{ height: "5rem" }} key={asset.id}>
                                            <TableCell>
                                                <Link to={'/assets/' + asset.id + '/view'}>{asset.name}-{asset.model}-{asset.deviceSubType?.deviceType?.name}</Link>
                                            </TableCell>
                                            <TableCell>
                                                <Select style={{ width: "100%" }}
                                                    className="icct-disabled-selection"
                                                    options={assetReadinessOptions}
                                                    onChange={(event, data) => { updateAssetReadiness(asset.id, data.value) }}
                                                    value={assetReadiness[asset.id]} />
                                            </TableCell>
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table> : "No assets"}
                            <div><Icon className="exclamation circle" />To update asset status you must create or resolve an asset problem.</div>
                        </FormField>
                        <FormField disabled={!team.id}>
                            <label>Status</label>
                            <Select multiple onChange={(_, data) => setTeamReason(data.value)} options={teamReadinessOptions} />
                        </FormField>
                        <FormField disabled={!team.id}>
                            <label>Team ready</label>
                            <Checkbox toggle checked={teamStatus} value={teamStatus} onChange={(_, data) => { setTeamStatus(data.checked) }} />
                        </FormField>
                        <FormField
                            control={TextArea}
                            label='Notes'
                            value={teamReadinessNotes}
                            name='notes'
                            onChange={(_, data) => setTeamReadinessNotes(data.value)}
                            alwaysPresent={true}
                        />
                    </Form>
                </div>

                {!isReadOnly && team.id && teamSubmitsReadiness(team) && (
                    <ActionButton
                        submitAction={submitReadiness}
                        resetHide={false}
                        resetText={'Cancel'}
                        showConfirmation={true}
                        confirmMsg={teamReason && teamStatus ?
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "left" }}>
                                <Icon className="exclamation triangle" color="red" size="big" />
                                <div style={{ margin: "0.6rem" }}>You have selected a status reason, but have marked your team as ready.</div>
                            </div>
                            : null}
                    />
                )}
            </div>
        </LoadingWrapper>
    )
}